import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Minus, Plus, X } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import { formatMoney } from '../../utils/currencyFormatter';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';
import ArbeitszeitPlanEditor from './shared/ArbeitszeitPlanEditor';
import ArbeitszeitPlanDocumentTable from './shared/ArbeitszeitPlanDocumentTable';
import PePapersDisclaimerGate from './shared/PePapersDisclaimerGate';
import { SondervereinbarungenField, renderSondervereinbarungenInDocument } from './shared/SondervereinbarungenField';
import {
    calculateWochenstundenFromPlan,
    calculateJahresarbeitstageFromPlan,
    calculateUrlaubstageNachBag,
    betriebArbeitstageProJahr,
    createArbeitszeitEntryFromPrevious,
    formatStdForDisplay,
    isArbeitszeitEntryComplete,
    type ArbeitszeitTagEntry,
} from '../../utils/pe-papers/arbeitszeitPlan';
import {
    LeistungslohnVariantToggle,
    type LeistungslohnVariant,
} from '../leistungslohn';

interface ArbeitsvertragModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any; // Repräsentiert das Dokument aus pepapers_documents
    onArchived?: () => void;
}

type Beschaeftigungsart = 'Vollzeit' | 'Teilzeit' | 'Minijob';
type GehaltMode = 'brutto' | 'stundenlohn';
type BetriebWochentage = '5' | '6';

interface ArbeitsvertragFormData {
    vorname: string;
    nachname: string;
    strasse: string;
    plz: string;
    ort: string;
    eintrittsdatum: string;
    beschaeftigungsart: Beschaeftigungsart;
    wochenstunden: string;
    hasProbezeit: boolean;
    probezeitMonate: string;
    urlaubBasisVollzeit: string;
    betriebWochentage: BetriebWochentage;
    jahresarbeitstage: string;
    urlaubstage: string;
    bruttoGrundgehalt: string;
    stundenlohn: string;
    isBefristet: boolean;
    befristungMonate: string;
    befristetBis: string;
}

const INITIAL_FORM_DATA: ArbeitsvertragFormData = {
    vorname: '',
    nachname: '',
    strasse: '',
    plz: '',
    ort: '',
    eintrittsdatum: '',
    beschaeftigungsart: 'Vollzeit',
    wochenstunden: '40',
    hasProbezeit: true,
    probezeitMonate: '6',
    urlaubBasisVollzeit: '20',
    betriebWochentage: '5',
    jahresarbeitstage: '52',
    urlaubstage: '4',
    bruttoGrundgehalt: '',
    stundenlohn: '',
    isBefristet: false,
    befristungMonate: '12',
    befristetBis: '',
};

const TEST_FORM_DATA: ArbeitsvertragFormData = {
    vorname: 'Maria',
    nachname: 'Mustermann',
    strasse: 'Musterstraße 12',
    plz: '80331',
    ort: 'München',
    eintrittsdatum: '2026-07-09',
    beschaeftigungsart: 'Vollzeit',
    wochenstunden: '38',
    hasProbezeit: true,
    probezeitMonate: '6',
    urlaubBasisVollzeit: '20',
    betriebWochentage: '5',
    jahresarbeitstage: '52',
    urlaubstage: '4',
    bruttoGrundgehalt: '2800',
    stundenlohn: '',
    isBefristet: true,
    befristungMonate: '6',
    befristetBis: '2027-01-08',
};

const DEV_TEST_PASSWORD = '90';

const A4_PREVIEW_PAGE_CLASS =
    'bg-white text-black my-4 mx-auto w-full max-w-[210mm] h-auto min-h-0 shadow-xl rounded-sm text-left block overflow-visible print:my-0 print:shadow-none';

/** Noble Slate + Print: Absätze/Blöcke nicht mittendrin zerschneiden */
const DOC_P_CLASS =
    'av-doc-p mb-3 text-justify text-slate-800 leading-relaxed break-inside-avoid page-break-inside-avoid';
const DOC_UL_CLASS =
    'av-doc-ul mb-3 ml-5 list-disc text-justify space-y-1 text-slate-800 break-inside-avoid page-break-inside-avoid';
const DOC_BLOCK_CLASS = 'av-doc-block break-inside-avoid page-break-inside-avoid';

/** PDF/Druck/Vorschau: Noble Slate — muss CAPTURE_CSS schlagen (!important) */
const AV_CONTRACT_PDF_CSS = `
  .av-a4-page {
    display: block !important;
    width: 100% !important;
    max-width: 210mm !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    color: #0f172a !important;
    font-size: 12.5px !important;
    line-height: 1.55 !important;
    padding: 12mm 14mm !important;
    background: #ffffff !important;
  }
  .av-a4-page * {
    box-sizing: border-box !important;
  }
  .av-a4-page .pdf-letterhead {
    width: 100% !important;
    text-align: center !important;
    border-bottom: 2px solid #1e293b !important;
    margin: 0 0 16px !important;
    padding: 0 0 12px !important;
  }
  .av-a4-page .pdf-letterhead h1 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 22px !important;
    font-weight: 900 !important;
    letter-spacing: 0.14em !important;
    text-transform: uppercase !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 4px !important;
    text-align: center !important;
  }
  .av-a4-page .pdf-letterhead h2 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.18em !important;
    text-transform: uppercase !important;
    color: #b48a56 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    text-align: center !important;
  }
  .av-meta-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin: 0 0 16px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  .av-meta-col {
    display: table-cell !important;
    width: 50% !important;
    vertical-align: top !important;
    padding: 12px 14px !important;
    font-size: 11px !important;
    line-height: 1.45 !important;
    color: #334155 !important;
    text-align: left !important;
  }
  .av-meta-col + .av-meta-col { border-left: 1px solid #e2e8f0 !important; }
  .av-meta-label {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.14em !important;
    color: #64748b !important;
    margin: 0 0 6px !important;
  }
  .av-meta-col strong { color: #0f172a !important; font-weight: 700 !important; }
  .av-meta-col p {
    width: 100% !important;
    margin: 0 !important;
    text-align: left !important;
    font-size: 11px !important;
    color: #334155 !important;
  }
  .av-doc-section {
    display: block !important;
    width: 100% !important;
    margin: 0 0 14px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  .av-a4-page h3,
  .av-doc-section > h3,
  .av-ll-page h3 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.06em !important;
    text-align: left !important;
    background: #1e293b !important;
    color: #f8fafc !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 8px 12px !important;
    margin: 0 0 10px !important;
  }
  .av-a4-page h4,
  .av-ll-page h4 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.04em !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    border-bottom: 1px solid #cbd5e1 !important;
    padding: 0 0 4px !important;
    margin: 14px 0 8px !important;
    text-align: left !important;
  }
  .av-a4-page p,
  .av-doc-p,
  .av-doc-section p,
  .av-ll-page p {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12.5px !important;
    line-height: 1.55 !important;
    color: #1e293b !important;
    text-align: justify !important;
    margin: 0 0 10px !important;
  }
  .av-doc-ul,
  .av-doc-section ul {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 0 10px !important;
    padding: 0 0 0 1.15em !important;
    list-style-type: disc !important;
    list-style-position: outside !important;
    color: #1e293b !important;
  }
  .av-doc-ul li,
  .av-doc-section li {
    margin: 0 0 0.25rem !important;
    padding: 0 !important;
    text-align: justify !important;
    font-size: 12.5px !important;
    line-height: 1.55 !important;
    color: #1e293b !important;
  }
  .av-table-wrap {
    display: block !important;
    width: 100% !important;
    margin: 0 0 12px !important;
  }
  .av-table-wrap p {
    text-align: left !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.08em !important;
    color: #64748b !important;
    margin: 0 0 6px !important;
  }
  .av-table-wrap table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    font-size: 11px !important;
  }
  .av-table-wrap thead th,
  .av-table-wrap th {
    background-color: #f1f5f9 !important;
    color: #0f172a !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.06em !important;
    padding: 6px 8px !important;
    border: 1px solid #e2e8f0 !important;
    border-bottom: 1px solid #cbd5e1 !important;
    text-align: left !important;
  }
  .av-table-wrap td {
    padding: 6px 8px !important;
    border: 1px solid #e2e8f0 !important;
    color: #334155 !important;
    text-align: left !important;
  }
  .av-sig-grid,
  .print-signatures-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin-top: 28px !important;
    margin-bottom: 0 !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  .av-sig-grid > div,
  .print-signatures-grid .signature-col {
    display: table-cell !important;
    width: 50% !important;
    padding-right: 16px !important;
    vertical-align: top !important;
  }
  .av-sig-grid > div:last-child,
  .print-signatures-grid .signature-col:last-child {
    padding-right: 0 !important;
    padding-left: 16px !important;
  }
  .av-sig-label {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    font-weight: 600 !important;
    color: #475569 !important;
    text-align: left !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .av-sig-rule {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    border: none !important;
    border-bottom: 2px solid #1e293b !important;
    margin-top: 2rem !important;
    margin-bottom: 0.25rem !important;
    padding: 0 !important;
  }
  .av-sig-role {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    font-weight: 400 !important;
    color: #475569 !important;
    text-align: left !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .av-sig-line {
    border-top: 1px solid #1e293b !important;
    padding-top: 6px !important;
    margin-top: 36px !important;
    font-size: 12.5px !important;
    color: #475569 !important;
    text-align: left !important;
  }
  .av-a4-page .doc-panel,
  .av-ll-page .doc-panel {
    display: block !important;
    width: 100% !important;
    border: 1.5px solid #334155 !important;
    background: #f8fafc !important;
    padding: 14px 16px !important;
    margin: 12px 0 0 !important;
    box-sizing: border-box !important;
  }
  .av-a4-page .doc-panel h4,
  .av-ll-page .doc-panel h4 {
    text-align: center !important;
    border-bottom: 1px solid #cbd5e1 !important;
    margin: 0 0 12px !important;
    padding: 0 0 8px !important;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .av-a4-page {
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      min-height: 0 !important;
      page-break-after: always !important;
      break-after: page !important;
    }
    .av-a4-page:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
    .pepapers-print-break {
      page-break-before: always !important;
      break-before: page !important;
    }
  }
`;

/** Isolierter Print-CSS-Block (1:1 wie ConsultationQuote / GoBD) */
const AV_PRINT_CSS = `
${AV_CONTRACT_PDF_CSS}
html, body {
  margin: 0;
  padding: 0;
  background: #ffffff;
}
`;

const BESCHAEFTIGUNG_LABEL: Record<Beschaeftigungsart, string> = {
    Vollzeit: 'Vollzeitkraft',
    Teilzeit: 'Teilzeitkraft',
    Minijob: 'Minijob-Beschäftigte/r',
};

const STEP_LABELS: Record<number, string> = {
    1: 'Mitarbeiterdaten',
    2: 'Konditionen',
    3: 'Vergütung & Leistungskomponenten',
    4: 'Vorschau & Druck',
};

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const NUMBER_INPUT_CLASS =
    `${INPUT_CLASS} appearance-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]`;

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const HELPER_TEXT_CLASS = 'text-sm text-pe-muted dark:text-slate-400 mt-1.5 leading-relaxed';

const READONLY_INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-accent-text dark:text-[#E6C299] px-4 py-2.5 text-sm cursor-not-allowed opacity-90';

const STEP_BTN_CLASS =
    'flex items-center justify-center w-10 shrink-0 rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-muted dark:text-slate-400 hover:text-[#E6C299] hover:border-[#E6C299]/50 transition-colors';

const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const WOCHENFAKTOR_FALLBACK = 4.33;

const LEISTUNGSLOHN_MULTIPLIKATOR_HINWEIS =
    'Für die Sollumsatzberechnung gilt jeweils der aktuelle Multiplikator. Dieser ist variabel und richtet sich nach der betrieblichen Team- und Kostenstruktur; eine feste Höhe wird in dieser Vereinbarung nicht festgelegt.';

const LEISTUNGSLOHN_PROVISION_HINWEIS =
    'Die Provisionssätze für Dienstleistung und Verkauf sind ebenfalls variabel und richten sich nach den jeweils aktuellen betrieblichen Regelungen. Eine feste Prozentangabe wird in dieser Vereinbarung nicht festgelegt. Verkaufsprovisionen werden nur gewährt, wenn das Umsatzsoll des Monats insgesamt überschritten wurde.';

const LOHN_WARNUNG =
    'Bitte trage ein gültiges Monatsbruttogehalt bzw. einen Stundenlohn ein, um den Vertrag zu generieren';

/** de-DE Beträge: "2.800,00" / "2800,5" / "2800" → Zahl */
const parseNum = (value: string): number => {
    const raw = String(value || '').trim();
    if (!raw) return 0;
    const normalized = raw.includes(',')
        ? raw.replace(/\./g, '').replace(',', '.')
        : raw.replace(/\s/g, '');
    const num = parseFloat(normalized);
    return Number.isNaN(num) ? 0 : num;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

const formatNumber2 = (value: number): string => {
    if (!value || Number.isNaN(value)) return '—';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatGermanDate = (isoString: string): string => {
    if (!isoString.trim()) return '____________';
    const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return `${match[3]}.${match[2]}.${match[1]}`;
    return isoString;
};

const calculateBefristetBis = (eintrittsdatum: string, months: number): string => {
    const match = eintrittsdatum.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match || months < 1) return '';
    const date = new Date(`${match[1]}-${match[2]}-${match[3]}T12:00:00`);
    date.setMonth(date.getMonth() + months);
    date.setDate(date.getDate() - 1);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const formatCurrencyAmount = (value: string | number, currencyCode: string = 'EUR'): string => {
    const num = typeof value === 'number' ? value : parseNum(String(value));
    if (Number.isNaN(num) || num === 0) return '____________';
    return formatMoney(num, currencyCode, 2);
};

const formatCurrencyInputValue = (value: string): string => {
    if (!value.trim()) return '';
    const num = parseNum(value);
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const WOCHENSTUNDEN_BY_BESCHAEFTIGUNGART: Record<Beschaeftigungsart, string> = {
    Vollzeit: '40',
    Teilzeit: '30',
    Minijob: '10',
};

const ArbeitsvertragModal: React.FC<ArbeitsvertragModalProps> = ({ isOpen, onClose, initialData, onArchived }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<ArbeitsvertragFormData>(INITIAL_FORM_DATA);
    const [leistungslohnAktiv, setLeistungslohnAktiv] = useState(false);
    const [leistungslohnVariant, setLeistungslohnVariant] = useState<LeistungslohnVariant>('mono');
    const [arbeitsplan, setArbeitsplan] = useState<ArbeitszeitTagEntry[]>([createArbeitszeitEntryFromPrevious()]);
    const [sondervereinbarungen, setSondervereinbarungen] = useState('');
    const wochenstundenManuellRef = useRef(false);
    const jahresarbeitstageManuellRef = useRef(false);
    const [gehaltMode, setGehaltMode] = useState<GehaltMode>('brutto');
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
    const [apiError, setApiError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
    const dateInputRef = useRef<HTMLInputElement>(null);
    const printContainerRef = useRef<HTMLDivElement>(null);

    const clientData = useAppStore((state: any) => state.clientData);
    const settings = useAppStore((state: any) => state.settings);
    const calc = useCalculationsSelector();
    const currencyCode = calc.currencyCode || 'EUR';
    const currencySuffix = currencyCode;
    const formatCurrency = (value: string | number) => formatCurrencyAmount(value, currencyCode);

    const wochensatz = useMemo(() => {
        const fromClient = Number(clientData?.wochensatz);
        if (Number.isFinite(fromClient) && fromClient > 0) return fromClient;
        const fromSettings = Number(settings?.global?.Wochensatz);
        if (Number.isFinite(fromSettings) && fromSettings > 0) return fromSettings;
        return WOCHENFAKTOR_FALLBACK;
    }, [clientData?.wochensatz, settings?.global?.Wochensatz]);

    const berechnetePlanWochenstunden = useMemo(
        () => calculateWochenstundenFromPlan(arbeitsplan, wochensatz),
        [arbeitsplan, wochensatz]
    );

    const berechnetePlanJahresarbeitstage = useMemo(
        () => calculateJahresarbeitstageFromPlan(arbeitsplan),
        [arbeitsplan]
    );

    const betriebJahresarbeitstage = useMemo(
        () => betriebArbeitstageProJahr(formData.betriebWochentage === '6' ? 6 : 5),
        [formData.betriebWochentage]
    );

    const employerName = clientData?.salonname || clientData?.firmenname || 'Salon';
    const employerStreet = clientData?.strasse || '';
    const employerPlz = clientData?.plz || '';
    const employerOrt = clientData?.ort || '';
    const employerRep = clientData?.unternehmername || '';

    // Cloud Function verlangt Zahlen — Werte aus aktuellem Halbjahr (Fallback 10)
    const halbjahrKey = useMemo(
        () => (new Date().getMonth() < 6 ? 'erstesHalbjahr' : 'zweitesHalbjahr') as
            'erstesHalbjahr' | 'zweitesHalbjahr',
        []
    );
    const provisionDienstleistung = Number(
        clientData?.leistungsprovision?.[halbjahrKey]?.provisionDienstleistung ?? 10
    );
    const provisionVerkauf = Number(
        clientData?.leistungsprovision?.[halbjahrKey]?.provisionVerkauf ?? 10
    );
    const lohnfaktorAktiv = Number(calc.lohnfaktorAktiv) || 0;

    const employeeFullName = [formData.vorname, formData.nachname].filter(Boolean).join(' ') || '____________';
    const employeeAddress = [formData.strasse, [formData.plz, formData.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '____________';

    const berechneteMonatsstunden = useMemo(() => {
        return round2(parseNum(formData.wochenstunden) * wochensatz);
    }, [formData.wochenstunden, wochensatz]);

    const probezeitEnde = useMemo(() => {
        if (!formData.hasProbezeit) return '';
        const monate = Math.round(parseNum(formData.probezeitMonate));
        if (monate < 1 || monate > 6) return '';
        return calculateBefristetBis(formData.eintrittsdatum, monate);
    }, [formData.hasProbezeit, formData.probezeitMonate, formData.eintrittsdatum]);

    const sondervereinbarungenText = sondervereinbarungen.trim();
    const hasSondervereinbarungen = sondervereinbarungenText.length > 0;

    const { effektivesBrutto, effektiverStundenlohn } = useMemo(() => {
        const monatsstunden = berechneteMonatsstunden;
        if (gehaltMode === 'brutto') {
            const brutto = parseNum(formData.bruttoGrundgehalt);
            const stundenlohn = monatsstunden > 0 ? round2(brutto / monatsstunden) : 0;
            return { effektivesBrutto: brutto, effektiverStundenlohn: stundenlohn };
        }
        const stundenlohn = parseNum(formData.stundenlohn);
        const brutto = round2(stundenlohn * monatsstunden);
        return { effektivesBrutto: brutto, effektiverStundenlohn: stundenlohn };
    }, [gehaltMode, formData.bruttoGrundgehalt, formData.stundenlohn, berechneteMonatsstunden]);

    const wochenstundenVertragAnzeige = useMemo(() => {
        return formData.wochenstunden.trim()
            ? formatNumber2(parseNum(formData.wochenstunden))
            : '____';
    }, [formData.wochenstunden]);

    const urlaubRawBerechnet = useMemo(() => {
        const basis = parseNum(formData.urlaubBasisVollzeit || '20');
        const jahresTage = parseNum(formData.jahresarbeitstage);
        if (basis <= 0 || jahresTage <= 0 || betriebJahresarbeitstage <= 0) return 0;
        return round2((basis / betriebJahresarbeitstage) * jahresTage);
    }, [formData.urlaubBasisVollzeit, formData.jahresarbeitstage, betriebJahresarbeitstage]);

    const berechneterUrlaubstage = useMemo(
        () => calculateUrlaubstageNachBag(
            parseNum(formData.urlaubBasisVollzeit || '20'),
            betriebJahresarbeitstage,
            parseNum(formData.jahresarbeitstage)
        ),
        [formData.urlaubBasisVollzeit, formData.jahresarbeitstage, betriebJahresarbeitstage]
    );

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setFormData(INITIAL_FORM_DATA);
            setLeistungslohnAktiv(false);
            setArbeitsplan([createArbeitszeitEntryFromPrevious()]);
            setSondervereinbarungen('');
            wochenstundenManuellRef.current = false;
            jahresarbeitstageManuellRef.current = false;
            setGehaltMode('brutto');
            setInvalidFields(new Set());
            setDisclaimerAccepted(false);
            setGeneratedHtml(null);
            setIsLoading(false);
            setApiError(false);
            setIsSaving(false);
            setArchiveFeedback('idle');
            return;
        }

        setCurrentStep(1);
        setDisclaimerAccepted(false);
        setInvalidFields(new Set());
        setGeneratedHtml(null);
        setIsLoading(false);
        setApiError(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            const archivedFormData = initialData.formData || {};
            const archivedPlan: ArbeitszeitTagEntry[] = initialData.arbeitsplan?.length
                ? initialData.arbeitsplan
                : [createArbeitszeitEntryFromPrevious()];
            const mergedFormData: ArbeitsvertragFormData = {
                ...INITIAL_FORM_DATA,
                ...archivedFormData,
                betriebWochentage:
                    archivedFormData.betriebWochentage === '6' ? '6' : '5',
            };
            const planJahres = calculateJahresarbeitstageFromPlan(archivedPlan);
            if (planJahres > 0 && !archivedFormData.jahresarbeitstage) {
                mergedFormData.jahresarbeitstage = String(planJahres);
            } else if (
                !mergedFormData.jahresarbeitstage &&
                archivedFormData.arbeitstageProWoche
            ) {
                const legacyWoche = parseNum(String(archivedFormData.arbeitstageProWoche));
                if (legacyWoche > 0) {
                    mergedFormData.jahresarbeitstage = String(Math.round(legacyWoche * 52));
                }
            }
            setFormData(mergedFormData);
            setLeistungslohnAktiv(Boolean(initialData.leistungslohnAktiv));
            setLeistungslohnVariant(
                initialData.leistungslohnVariant === 'team' ||
                    clientData?.teamBonus?.bonusMode === 'team'
                    ? 'team'
                    : 'mono'
            );
            setArbeitsplan(archivedPlan);
            setSondervereinbarungen(initialData.sondervereinbarungen || '');
            jahresarbeitstageManuellRef.current = Boolean(archivedFormData.jahresarbeitstage);

            const stundenlohnVal = parseNum(archivedFormData.stundenlohn || '');
            const bruttoEmpty = !String(archivedFormData.bruttoGrundgehalt || '').trim();
            setGehaltMode(stundenlohnVal > 0 && bruttoEmpty ? 'stundenlohn' : 'brutto');

            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 4) {
                setCurrentStep(step);
            }
            if (typeof initialData.generatedHtml === 'string' && initialData.generatedHtml.trim()) {
                setGeneratedHtml(initialData.generatedHtml);
            }
            if (initialData.confirmedDisclaimer) {
                setDisclaimerAccepted(true);
            }
        } else {
            setFormData(INITIAL_FORM_DATA);
            setLeistungslohnAktiv(false);
            setLeistungslohnVariant(clientData?.teamBonus?.bonusMode === 'team' ? 'team' : 'mono');
            setArbeitsplan([createArbeitszeitEntryFromPrevious()]);
            setSondervereinbarungen('');
            wochenstundenManuellRef.current = false;
            jahresarbeitstageManuellRef.current = false;
            setGehaltMode('brutto');
        }
    }, [isOpen, initialData, clientData?.teamBonus?.bonusMode]);

    useEffect(() => {
        if (!isOpen || wochenstundenManuellRef.current) return;
        if (berechnetePlanWochenstunden > 0) {
            setFormData((prev) => ({
                ...prev,
                wochenstunden: formatStdForDisplay(berechnetePlanWochenstunden),
            }));
        }
    }, [isOpen, berechnetePlanWochenstunden]);

    useEffect(() => {
        if (!isOpen || jahresarbeitstageManuellRef.current) return;
        if (berechnetePlanJahresarbeitstage <= 0) return;
        const tage = String(berechnetePlanJahresarbeitstage);
        setFormData((prev) =>
            prev.jahresarbeitstage === tage ? prev : { ...prev, jahresarbeitstage: tage }
        );
    }, [isOpen, berechnetePlanJahresarbeitstage]);

    useEffect(() => {
        const berechneterUrlaub = berechneterUrlaubstage;
        setFormData((prev) =>
            prev.urlaubstage === String(berechneterUrlaub)
                ? prev
                : { ...prev, urlaubstage: String(berechneterUrlaub) }
        );
    }, [berechneterUrlaubstage]);

    useEffect(() => {
        if (!formData.isBefristet) return;

        const months = Math.round(parseNum(formData.befristungMonate));
        if (!formData.eintrittsdatum || months < 1 || months > 12) {
            setFormData((prev) => (prev.befristetBis === '' ? prev : { ...prev, befristetBis: '' }));
            return;
        }

        const computed = calculateBefristetBis(formData.eintrittsdatum, months);
        setFormData((prev) => (prev.befristetBis === computed ? prev : { ...prev, befristetBis: computed }));
    }, [formData.isBefristet, formData.eintrittsdatum, formData.befristungMonate]);

    useEffect(() => {
        if (!isOpen || currentStep !== 2) return;
        if (formData.wochenstunden.trim() || berechnetePlanWochenstunden > 0) return;

        const defaultHours = WOCHENSTUNDEN_BY_BESCHAEFTIGUNGART[formData.beschaeftigungsart];
        setFormData((prev) =>
            prev.wochenstunden === defaultHours ? prev : { ...prev, wochenstunden: defaultHours }
        );
    }, [isOpen, currentStep, formData.wochenstunden, formData.beschaeftigungsart, berechnetePlanWochenstunden]);

    const isEmpty = (value: string) => !value.trim();
    const isPositive = (value: string) => parseNum(value) > 0;

    const validateStep = (step: number): string[] => {
        const errors: string[] = [];

        if (step === 1) {
            (['vorname', 'nachname', 'strasse', 'plz', 'ort'] as const).forEach((field) => {
                if (isEmpty(formData[field])) errors.push(field);
            });
        }

        if (step === 2) {
            if (isEmpty(formData.eintrittsdatum)) errors.push('eintrittsdatum');
            const gueltigePlanEintraege = arbeitsplan.filter(isArbeitszeitEntryComplete);
            if (gueltigePlanEintraege.length === 0) errors.push('arbeitsplan');
            if (!isPositive(formData.wochenstunden)) errors.push('wochenstunden');
            if (formData.hasProbezeit) {
                const probeMonate = Math.round(parseNum(formData.probezeitMonate));
                if (probeMonate < 1 || probeMonate > 6) errors.push('probezeitMonate');
                if (isEmpty(probezeitEnde)) errors.push('probezeitEnde');
            }
            if (!isPositive(formData.urlaubBasisVollzeit)) errors.push('urlaubBasisVollzeit');
            if (!isPositive(formData.jahresarbeitstage)) errors.push('jahresarbeitstage');
            if (!isPositive(formData.urlaubstage)) errors.push('urlaubstage');
            if (formData.isBefristet) {
                const monate = Math.round(parseNum(formData.befristungMonate));
                if (monate < 1 || monate > 12) errors.push('befristungMonate');
                if (isEmpty(formData.befristetBis)) errors.push('befristetBis');
            }
        }

        if (step === 3) {
            if (gehaltMode === 'brutto') {
                if (!isPositive(formData.bruttoGrundgehalt)) errors.push('bruttoGrundgehalt');
            } else if (!isPositive(formData.stundenlohn)) {
                errors.push('stundenlohn');
            }
        }

        return errors;
    };

    const hasValidLohn = (): boolean =>
        gehaltMode === 'brutto'
            ? isPositive(formData.bruttoGrundgehalt)
            : isPositive(formData.stundenlohn);

    const markLohnInvalid = () => {
        setInvalidFields((prev) => {
            const next = new Set(prev);
            if (gehaltMode === 'brutto') {
                next.add('bruttoGrundgehalt');
                next.delete('stundenlohn');
            } else {
                next.add('stundenlohn');
                next.delete('bruttoGrundgehalt');
            }
            return next;
        });
    };

    const showLohnWarnung =
        invalidFields.has('bruttoGrundgehalt') || invalidFields.has('stundenlohn');

    const isFieldInvalid = (field: string) => invalidFields.has(field);

    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} ${ERROR_INPUT_RING}` : baseClass;

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const updateField = <K extends keyof ArbeitsvertragFormData>(key: K, value: ArbeitsvertragFormData[K]) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(key);
    };

    const handleBeschaeftigungsartChange = (art: Beschaeftigungsart) => {
        setFormData((prev) => ({
            ...prev,
            beschaeftigungsart: art,
            wochenstunden: wochenstundenManuellRef.current
                ? prev.wochenstunden
                : WOCHENSTUNDEN_BY_BESCHAEFTIGUNGART[art],
            hasProbezeit: art !== 'Minijob',
        }));
        clearFieldError('beschaeftigungsart');
        clearFieldError('wochenstunden');
    };

    const handleCurrencyBlur = (field: 'bruttoGrundgehalt' | 'stundenlohn') => {
        const formatted = formatCurrencyInputValue(formData[field]);
        if (formatted !== formData[field]) {
            updateField(field, formatted);
        }
    };

    const renderFieldError = (field: string) => {
        if (!isFieldInvalid(field)) return null;
        const isLohnField = field === 'bruttoGrundgehalt' || field === 'stundenlohn';
        return (
            <p className="text-[10px] text-red-400 mt-1">
                {isLohnField ? LOHN_WARNUNG : 'Dieses Feld ist erforderlich'}
            </p>
        );
    };

    const renderLohnWarnBanner = () =>
        showLohnWarnung ? (
            <div
                role="alert"
                className="rounded-xl border border-red-500/50 bg-red-950/40 px-4 py-3 text-sm font-semibold text-red-300"
            >
                {LOHN_WARNUNG}
            </div>
        ) : null;

    const adjustNumericField = (
        field: 'wochenstunden' | 'urlaubBasisVollzeit' | 'jahresarbeitstage' | 'befristungMonate' | 'probezeitMonate',
        direction: 1 | -1,
        step: number
    ) => {
        const current = formData[field];
        const base = current === '' ? 0 : parseNum(current);
        let next = base + direction * step;
        if (field === 'urlaubBasisVollzeit') {
            next = Math.max(0, Math.round(next));
            updateField(field, String(next));
            return;
        }
        if (field === 'jahresarbeitstage') {
            next = Math.max(1, Math.round(next));
            jahresarbeitstageManuellRef.current = true;
            updateField(field, String(next));
            return;
        }
        if (field === 'befristungMonate') {
            next = Math.min(12, Math.max(1, Math.round(next)));
            updateField(field, String(next));
            clearFieldError('befristungMonate');
            clearFieldError('befristetBis');
            return;
        }
        if (field === 'probezeitMonate') {
            next = Math.min(6, Math.max(1, Math.round(next)));
            updateField(field, String(next));
            clearFieldError('probezeitMonate');
            clearFieldError('probezeitEnde');
            return;
        }
        next = Math.max(0, round2(next));
        if (field === 'wochenstunden') {
            wochenstundenManuellRef.current = true;
        }
        updateField(field, String(next));
        clearFieldError(field);
    };

    const openDatePicker = (ref: React.RefObject<HTMLInputElement | null>) => {
        const el = ref.current;
        if (!el) return;
        if (typeof el.showPicker === 'function') {
            el.showPicker();
        } else {
            el.focus();
        }
    };

    const renderStepperInput = (
        field: 'wochenstunden' | 'urlaubBasisVollzeit' | 'jahresarbeitstage' | 'befristungMonate' | 'probezeitMonate',
        value: string,
        step: number,
        options?: { min?: number; max?: number }
    ) => (
        <div>
            <div className="flex items-stretch gap-2">
                <button
                    type="button"
                    onClick={() => adjustNumericField(field, -1, step)}
                    className={STEP_BTN_CLASS}
                    aria-label="Wert verringern"
                >
                    <Minus size={14} strokeWidth={2.5} />
                </button>
                <input
                    type="text"
                    inputMode="decimal"
                    min={options?.min ?? 0}
                    max={options?.max}
                    value={value}
                    onFocus={(e) => {
                        requestAnimationFrame(() => {
                            if (document.activeElement === e.currentTarget) e.currentTarget.select();
                        });
                    }}
                    onClick={(e) => {
                        if (e.detail > 1) return;
                        requestAnimationFrame(() => {
                            if (document.activeElement === e.currentTarget) e.currentTarget.select();
                        });
                    }}
                    onChange={(e) => {
                        let nextVal = e.target.value;
                        if (field === 'jahresarbeitstage') {
                            jahresarbeitstageManuellRef.current = true;
                            if (nextVal !== '') {
                                nextVal = String(Math.max(1, Math.round(parseNum(nextVal))));
                            }
                        }
                        if (field === 'befristungMonate' && nextVal !== '') {
                            const clamped = Math.min(12, Math.max(1, Math.round(parseNum(nextVal))));
                            nextVal = String(clamped);
                        }
                        if (field === 'probezeitMonate' && nextVal !== '') {
                            const clamped = Math.min(6, Math.max(1, Math.round(parseNum(nextVal))));
                            nextVal = String(clamped);
                        }
                        if (field === 'wochenstunden') {
                            wochenstundenManuellRef.current = true;
                        }
                        updateField(field, nextVal);
                    }}
                    className={getInputClass(`${NUMBER_INPUT_CLASS} flex-1 min-w-0 text-center`, field)}
                />
                <button
                    type="button"
                    onClick={() => adjustNumericField(field, 1, step)}
                    className={STEP_BTN_CLASS}
                    aria-label="Wert erhöhen"
                >
                    <Plus size={14} strokeWidth={2.5} />
                </button>
            </div>
            {renderFieldError(field)}
        </div>
    );

    const renderCurrencyInput = (
        field: 'bruttoGrundgehalt' | 'stundenlohn',
        value: string
    ) => (
        <div>
            <div className="relative flex items-center">
                <input
                    type="text"
                    inputMode="decimal"
                    value={value}
                    onChange={(e) => updateField(field, e.target.value)}
                    onBlur={() => handleCurrencyBlur(field)}
                    className={getInputClass(`${INPUT_CLASS} pr-10`, field)}
                />
                <span className="absolute right-4 text-pe-muted dark:text-slate-400 text-sm pointer-events-none">{currencySuffix}</span>
            </div>
            {renderFieldError(field)}
        </div>
    );

    const handleWeiter = () => {
        const errors = validateStep(currentStep);
        if (errors.length > 0) {
            setInvalidFields(new Set(errors));
            return;
        }
        setInvalidFields(new Set());
        setCurrentStep((s) => s + 1);
    };

    const handleGenerierenClick = async () => {
        const errors = validateStep(3);
        if (errors.length > 0 || !hasValidLohn()) {
            if (errors.length > 0) setInvalidFields(new Set(errors));
            else markLohnInvalid();
            return;
        }
        setInvalidFields(new Set());

        setIsLoading(true);
        setApiError(false);
        setGeneratedHtml(null);

        try {
            const generateContractFn = httpsCallable(functions, 'generatePePapersArbeitsvertrag');
            const response = await generateContractFn({
                mitarbeiter: formData,
                salon: {
                    ...clientData,
                    salonname: clientData?.salonname || clientData?.firmenname || 'Mein Salon',
                    unternehmername: clientData?.unternehmername || '',
                    strasse: clientData?.strasse || '',
                    plz: clientData?.plz || '',
                    ort: clientData?.ort || '',
                },
                currencyCode,
                konditionen: {
                    ...formData,
                    gehaltMode,
                    // Cloud Function: stundenMode muss 'wochenstunden' | 'monatsstunden' sein
                    // UI erfasst ausschließlich Wochenstunden → Mode fest auf wochenstunden
                    stundenMode: 'wochenstunden',
                    isBefristet: formData.isBefristet,
                    befristungMonate: Number(formData.befristungMonate),
                    befristetBis: formData.befristetBis,
                    arbeitszeitWert: parseNum(formData.wochenstunden),
                    arbeitsplan,
                    wochensatz,
                    probezeitEnde,
                    sondervereinbarungen: sondervereinbarungenText || null,
                    gehaltWert:
                        gehaltMode === 'brutto'
                            ? parseNum(formData.bruttoGrundgehalt)
                            : parseNum(formData.stundenlohn),
                    urlaubstage: Number(formData.urlaubstage),
                    currencyCode,
                },
                leistungslohn: {
                    aktiv: leistungslohnAktiv,
                    variant: leistungslohnVariant,
                    // Pflichtzahlen für Cloud-Function-Validierung (auch bei „variabel“-Text)
                    provisionDienstleistung:
                        Number.isFinite(provisionDienstleistung) && provisionDienstleistung >= 0
                            ? provisionDienstleistung
                            : 10,
                    provisionVerkauf:
                        Number.isFinite(provisionVerkauf) && provisionVerkauf >= 0
                            ? provisionVerkauf
                            : 10,
                    lohnfaktorAktiv: Number.isFinite(lohnfaktorAktiv) ? lohnfaktorAktiv : 0,
                    multiplikatorVariabel: true,
                    provisionenVariabel: true,
                    hinweis:
                        leistungslohnVariant === 'team'
                            ? 'Teamleistungslohn: Salon-Zielumsatz als Schwelle, Team-Bonus-Topf, Verteilung proportional zum produktiven Grundgehalt.'
                            : 'Im Vertragstext keine festen Multiplikator- oder Provisionswerte nennen. Es gilt jeweils der aktuelle Multiplikator sowie die jeweils aktuellen, variablen Provisionssätze für Dienstleistung und Verkauf.',
                },
            });

            const data = response.data as { html?: string } | null;
            if (data?.html) {
                setGeneratedHtml(data.html);
                setCurrentStep(4);
            } else {
                throw new Error('Leere API-Antwort');
            }
        } catch (error: any) {
            console.error('CRITICAL PEPAPERS API ERROR:', error);
            setApiError(true);
            setIsLoading(false);
            alert(
                'KI-Verbindung fehlgeschlagen! Grund: ' +
                    (error.message || 'Unbekannter API-Fehler') +
                    '. Schau jetzt in die Browser-Konsole (F12)!'
            );
            return;
        } finally {
            setIsLoading(false);
        }
    };

    const handleZurueck = () => {
        setInvalidFields(new Set());
        setCurrentStep((s) => s - 1);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setFormData(INITIAL_FORM_DATA);
        setLeistungslohnAktiv(false);
        setArbeitsplan([createArbeitszeitEntryFromPrevious()]);
        setSondervereinbarungen('');
        wochenstundenManuellRef.current = false;
        jahresarbeitstageManuellRef.current = false;
        setGehaltMode('brutto');
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setGeneratedHtml(null);
        setIsLoading(false);
        setApiError(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        onClose();
    };

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const handlePrintIsolated = () => {
        if (isActionLocked) return;
        if (!hasValidLohn()) {
            markLohnInvalid();
            return;
        }

        const iframe = document.createElement('iframe');
        // Messbar (nicht 0×0), sonst stimmen scrollHeight/Fit nicht — wie ConsultationQuote
        iframe.style.cssText =
            'position:fixed;left:-12000px;top:0;width:210mm;height:297mm;border:none;visibility:hidden;';
        document.body.appendChild(iframe);

        const win = iframe.contentWindow;
        if (!win) {
            document.body.removeChild(iframe);
            return;
        }

        const printContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-arbeitsvertrag-print')?.innerHTML ||
            '';

        win.document.open();
        win.document.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"/><title>Arbeitsvertrag</title>
<style>${AV_PRINT_CSS}</style></head><body>${printContent}</body></html>`);
        win.document.close();

        const runPrint = () => {
            try {
                const doc = win.document;
                const mmToPx = 96 / 25.4;
                const printableHpx = 297 * mmToPx;
                const pages = Array.from(doc.querySelectorAll<HTMLElement>('.av-a4-page'));
                pages.forEach((page) => {
                    page.style.width = '100%';
                    page.style.maxWidth = '100%';
                    page.style.boxSizing = 'border-box';
                    page.style.transformOrigin = 'top left';
                    page.style.transform = '';
                    page.style.zoom = '';
                    const h = Math.max(page.scrollHeight, page.offsetHeight, 1);
                    if (h <= printableHpx * 1.02) return;
                    // Volle Blattbreite: erst breiter machen, dann scale → kein Rechts-Loch
                    const fit = Math.max(0.78, Math.min(1, (printableHpx / h) * 0.98));
                    page.style.width = `${(100 / fit).toFixed(3)}%`;
                    page.style.transform = `scale(${fit})`;
                });
            } catch (err) {
                console.warn('Arbeitsvertrag Print-Fit fehlgeschlagen:', err);
            }
            win.focus();
            win.print();
            setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 1000);
        };

        setTimeout(runPrint, 450);
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        if (!hasValidLohn()) {
            markLohnInvalid();
            return;
        }
        const html =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-arbeitsvertrag-print')?.innerHTML ||
            '';
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `Arbeitsvertrag_${dateIso}.pdf`,
                extraCss: AV_CONTRACT_PDF_CSS,
                scrubMode: 'minimal',
                splitPrintBreaks: true,
                fitSectionsToPage: true,
                letterheadOnBreaks: false,
                capturePadPx: 0,
            });
        } catch (err: any) {
            console.error('PEPapers Arbeitsvertrag PDF-Download fehlgeschlagen:', err);
            window.alert(
                'PDF konnte nicht heruntergeladen werden: ' +
                    (String(err?.message || '').match(/setEnd|Range|offset/i)
                        ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                        : err?.message || 'Unbekannter Fehler')
            );
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleDevTestFill = () => {
        const password = window.prompt('Test-Passwort eingeben:');
        if (password !== DEV_TEST_PASSWORD) return;

        setFormData(TEST_FORM_DATA);
        setLeistungslohnAktiv(true);
        setArbeitsplan([createArbeitszeitEntryFromPrevious()]);
        setSondervereinbarungen('');
        wochenstundenManuellRef.current = false;
        jahresarbeitstageManuellRef.current = false;
        setGehaltMode('brutto');
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setCurrentStep(4);
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;
        if (!asDraft && !hasValidLohn()) {
            markLohnInvalid();
            return;
        }

        setIsSaving(true);
        setArchiveFeedback('idle');

        const htmlContent =
            generatedHtml ||
            printContainerRef.current?.innerHTML ||
            'Lokales Standard-Muster verwendet';

        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'arbeitsvertrag',
                status: asDraft ? 'entwurf' : 'final',
                salonName: clientData?.salonname || clientData?.firmenname || 'Unbekannter Salon',
                mitarbeiterName: `${formData.vorname} ${formData.nachname}`.trim(),
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    formData,
                    arbeitsplan,
                    wochensatz,
                    sondervereinbarungen: sondervereinbarungenText || null,
                    leistungslohnAktiv,
                    leistungslohnVariant,
                    generatedHtml: htmlContent,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderSegmentedControl = <T extends string>(
        value: T,
        options: { key: T; label: string }[],
        onChange: (key: T) => void
    ) => (
        <div className="flex rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-1 gap-1">
            {options.map((opt) => (
                <button
                    key={opt.key}
                    type="button"
                    onClick={() => onChange(opt.key)}
                    className={`flex-1 px-3 py-2 rounded-md text-[10px] font-bold uppercase tracking-widest transition-colors ${
                        value === opt.key
                            ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                            : 'text-stone-500 dark:text-slate-500 hover:text-stone-700 dark:hover:text-slate-300 border border-transparent'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );

    const renderLocalFallbackMainContract = () => {
        const sonstigesParagraphNum = hasSondervereinbarungen ? 6 : 5;
        const beschaeftigungLabel =
            BESCHAEFTIGUNG_LABEL[formData.beschaeftigungsart] ||
            formData.beschaeftigungsart ||
            'Mitarbeiter/in';

        return (
        <>
        <div className={`av-a4-page text-slate-900 ${A4_PREVIEW_PAGE_CLASS}`}>
            <div className="pdf-letterhead text-center">
                <h1>Arbeitsvertrag</h1>
                <h2>PreisExpert · PEPapers</h2>
            </div>

            <div className="av-meta-grid">
                <div className="av-meta-col">
                    <p className="av-meta-label">Arbeitgeber</p>
                    <p className="mb-0">
                        <strong>{employerName}</strong>
                        {employerStreet ? (
                            <>
                                <br />
                                {employerStreet}
                            </>
                        ) : null}
                        {employerPlz || employerOrt ? (
                            <>
                                <br />
                                {[employerPlz, employerOrt].filter(Boolean).join(' ')}
                            </>
                        ) : null}
                        {employerRep ? (
                            <>
                                <br />
                                <span className="text-slate-600">Vertreten durch: {employerRep}</span>
                            </>
                        ) : null}
                    </p>
                </div>
                <div className="av-meta-col">
                    <p className="av-meta-label">Arbeitnehmer/in</p>
                    <p className="mb-0">
                        <strong>{employeeFullName}</strong>
                        <br />
                        {employeeAddress}
                    </p>
                </div>
            </div>

            <p className={`${DOC_P_CLASS} mb-5`}>
                Zwischen den oben genannten Parteien wird folgender Arbeitsvertrag geschlossen:
            </p>

            <div className="av-doc-section">
            <h3>§ 1 Beginn und Tätigkeit</h3>
            <p className={DOC_P_CLASS}>
                Das Arbeitsverhältnis beginnt am <strong>{formatGermanDate(formData.eintrittsdatum)}</strong>.
                Die Beschäftigung erfolgt als <strong>{beschaeftigungLabel}</strong> im Salonbetrieb des Arbeitgebers.
            </p>
            {formData.hasProbezeit && (
                <p className={DOC_P_CLASS}>
                    Die ersten <strong>{formData.probezeitMonate || '6'}</strong> Monate gelten als Probezeit und enden am{' '}
                    <strong>{formatGermanDate(probezeitEnde)}</strong>. Während der Probezeit kann das Arbeitsverhältnis
                    mit einer Frist von zwei Wochen gekündigt werden.
                </p>
            )}
            </div>

            <div className="av-doc-section">
            <h3>
                § 2 Arbeitszeit, Mehrarbeit und Arbeitszeitkonto
            </h3>
            <p className={DOC_P_CLASS}>
                <strong>(1) Wöchentliche Arbeitszeit:</strong> Die regelmäßige wöchentliche Arbeitszeit beträgt{' '}
                <strong>{wochenstundenVertragAnzeige}</strong> Stunden ohne Ruhepausen. Die Lage und Verteilung der
                täglichen Arbeitszeit richtet sich nach den betrieblichen Notwendigkeiten und dem jeweiligen Dienstplan
                des Salons.
            </p>
            <div className={DOC_BLOCK_CLASS}>
                <ArbeitszeitPlanDocumentTable
                    plan={arbeitsplan}
                    title="Wöchentlicher Arbeitsplan"
                    wochensatz={wochensatz}
                    wochenstundenOverride={
                        parseNum(formData.wochenstunden) > 0
                            ? parseNum(formData.wochenstunden)
                            : null
                    }
                />
            </div>
            <p className={DOC_P_CLASS}>
                <strong>(2) Anordnung von Überstunden:</strong> Der Arbeitnehmer verpflichtet sich, bei betrieblicher
                Notwendigkeit (z. B. bei hohem Kundenaufkommen, Samstagsarbeit, unvorhersehbarem Personalausfall) auf
                Anordnung der Salonleitung im gesetzlich zulässigen Rahmen Mehrarbeit bzw. Überstunden zu leisten.
                Überstunden werden nur berücksichtigt und vergütet, wenn sie von der Salonleitung ausdrücklich angeordnet
                oder unverzüglich am selben Tag schriftlich bzw. digital genehmigt wurden. Eigenmächtig geleistete
                Mehrarbeit begründet keinen Anspruch.
            </p>
            </div>
        </div>

        <div className={`pepapers-print-break av-a4-page text-slate-900 ${A4_PREVIEW_PAGE_CLASS}`}>
            <div className="av-doc-section">
            <h3>§ 2 Arbeitszeit, Mehrarbeit und Arbeitszeitkonto (Fortsetzung)</h3>
            <p className={DOC_P_CLASS}>
                <strong>(3) Führung eines Arbeitszeitkontos:</strong> Zur Flexibilisierung der Arbeitszeit wird für den
                Arbeitnehmer ein Arbeitszeitkonto geführt. Auf diesem Konto werden geleistete Plus- sowie Minusstunden
                erfasst.
            </p>
            <ul className={DOC_UL_CLASS}>
                <li>
                    <strong>Obergrenzen:</strong> Das Zeitkonto darf ein Guthaben von maximal +20 Stunden sowie eine
                    Schuld von maximal -10 Stunden nicht überschreiten.
                </li>
                <li>
                    <strong>Erfassung:</strong> Die Erfassung erfolgt minutengenau über das betriebsinterne
                    Zeiterfassungssystem.
                </li>
            </ul>
            <p className={DOC_P_CLASS}>
                <strong>(4) Ausgleich von Guthabenstunden (Primat des Freizeitausgleichs):</strong> Ein Guthaben auf dem
                Arbeitszeitkonto wird vorrangig durch bezahlte Freizeit im Verhältnis 1:1 ausgeglichen.
            </p>
            <ul className={DOC_UL_CLASS}>
                <li>
                    <strong>Frist &amp; Lage:</strong> Der Freizeitausgleich soll zeitnah, in der Regel innerhalb eines
                    Ausgleichszeitraums von 3 Monaten (spätestens innerhalb von 6 Monaten) erfolgen.
                </li>
                <li>
                    <strong>Festlegung:</strong> Der Zeitpunkt des Freizeitausgleichs wird unter Berücksichtigung der
                    betrieblichen Belange und der Wünsche des Arbeitnehmers von der Salonleitung festgelegt (vorzugsweise
                    in umsatzschwächeren Randzeiten oder als ganze Tage).
                </li>
                <li>
                    <strong>Barauszahlung:</strong> Eine Auszahlung von Überstunden erfolgt nur ausnahmsweise, wenn ein
                    Freizeitausgleich aus zwingenden betrieblichen Gründen nicht möglich ist oder das Arbeitsverhältnis
                    beendet wird.
                </li>
            </ul>
            <p className={DOC_P_CLASS}>
                <strong>(5) Ausschlussfrist / Verfall:</strong> Geleistete Überstunden, die nicht innerhalb von drei
                Monaten nach Ablauf des Monats, in dem sie entstanden sind, in Textform gegenüber der Salonleitung
                geltend gemacht werden, verfallen ersatzlos.
            </p>
            </div>

            <div className="av-doc-section">
            <h3>§ 3 Vergütung</h3>
            <p className={DOC_P_CLASS}>
                Der Arbeitnehmer/in erhält ein monatliches Brutto-Grundgehalt in Höhe von{' '}
                <strong>{formatCurrency(effektivesBrutto)}</strong> sowie einen Stundenlohn von{' '}
                <strong>{effektiverStundenlohn > 0 ? `${formatCurrency(effektiverStundenlohn)} / Std.` : '____________'}</strong>,
                zahlbar jeweils zum Monatsende.
            </p>
            </div>

            <div className="av-doc-section">
            <h3>§ 4 Urlaub</h3>
            <p className={DOC_P_CLASS}>
                Der Arbeitnehmer/in hat Anspruch auf <strong>{formData.urlaubstage || '20'}</strong> Arbeitstage Erholungsurlaub
                pro Kalenderjahr, soweit nicht gesetzlich ein höherer Anspruch besteht.
            </p>
            </div>

            {renderSondervereinbarungenInDocument(sondervereinbarungenText, 'vertrag', 5)}

            <div className="av-doc-section">
            <h3>
                § {sonstigesParagraphNum} Sonstiges
            </h3>
            <p className={DOC_P_CLASS}>
                Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform. Sollte eine Bestimmung
                dieser Vereinbarung unwirksam sein, wird die Wirksamkeit der übrigen Bestimmungen hiervon nicht
                berührt. Die Parteien verpflichten sich, die unwirksame Bestimmung durch eine Vereinbarung zu
                ersetzen, die der unwirksamen Bestimmung in Interessenlage und Bedeutung möglichst nahe kommt.
            </p>
            </div>

            <div className="av-sig-grid">
                <div>
                    <div className="av-sig-label">Ort, Datum</div>
                    <div className="av-sig-rule" aria-hidden="true" />
                    <div className="av-sig-role">Unterschrift Arbeitgeber/in</div>
                </div>
                <div>
                    <div className="av-sig-label">Ort, Datum</div>
                    <div className="av-sig-rule" aria-hidden="true" />
                    <div className="av-sig-role">Unterschrift Arbeitnehmer/in</div>
                </div>
            </div>
        </div>
        </>
        );
    };

    const isTeamLeistungslohn = leistungslohnVariant === 'team';

    const renderLocalFallbackPage2 = () => (
        <>
            {isTeamLeistungslohn ? (
                <>
                    <div className={`pepapers-print-break av-a4-page av-ll-page ${A4_PREVIEW_PAGE_CLASS}`}>
                        <div className="av-doc-section">
                            <h3>
                                PreisExpert – Teamleistungslohnsystem | Bitte dem/der Mitarbeiter/in zur Information
                                aushändigen
                            </h3>
                        </div>
                        <div className="av-doc-section">
                            <h4>Funktionen</h4>
                            <p className="av-doc-p">
                                <strong>1. Betriebswirtschaftliche Zielermittlung (Salon-Zielumsatz):</strong>
                                <br />
                                Im PreisExpert-Kalkulationstool wird auf Basis einer transparenten Kostenkalkulation der
                                monatliche Salon-Zielumsatz ermittelt. Dieser Betrag deckt alle laufenden Gesamtkosten des
                                Salons sowie die betriebswirtschaftlich notwendigen Rücklagen ab.
                            </p>
                            <p className="av-doc-p">
                                <strong>2. Umsatzerfassung &amp; Bonus-Schwelle:</strong>
                                <br />
                                Im System werden monatlich die tatsächlich erbrachten Umsätze des Salons erfasst (Bedien-
                                und Verkaufsumsatz). Ein Teamleistungslohn wird ausschließlich dann ausgelöst, wenn das
                                Team in der Gesamtheit den kalkulierten Salon-Zielumsatz übertrifft.
                            </p>
                            <p className="av-doc-p">
                                <strong>3. Team-Bonus-Topf:</strong>
                                <br />
                                Wird der kalkulierte Salon-Zielumsatz übertroffen, fließt ein vom Arbeitgeber festgelegter
                                Prozentsatz des erwirtschafteten Überschusses in einen gemeinsamen Team-Bonus-Topf.
                            </p>
                            <p className="av-doc-p">
                                <strong>4. Keine fiktiven Ausgleiche bei Abwesenheiten:</strong>
                                <br />
                                Im Teamleistungslohnsystem fließen ausschließlich real erwirtschaftete Salonumsätze ein.
                                Für Urlaubs-, Fehl- oder Krankheitstage werden keine fiktiven Umsätze gutgeschrieben;
                                maßgeblich ist das tatsächliche Gesamtergebnis des Salons.
                            </p>
                            <p className="av-doc-p">
                                <strong>5. Verteilungsschlüssel (Produktives Grundgehalt):</strong>
                                <br />
                                Der erwirtschaftete Team-Bonus-Topf wird unter den berechtigten Mitarbeiter*innen
                                aufgeteilt. Die Verteilung erfolgt standardmäßig proportional zum vertraglich vereinbarten
                                produktiven Brutto-Grundgehalt. Rein unproduktive Gehaltsbestandteile (z. B. für
                                administrative Leitungsaufgaben) bleiben in der Verteilungsbasis unberücksichtigt.
                                Optionale Einbindung: Der Arbeitgeber kann im System betriebsindividuell festlegen, ob und
                                mit welcher Gewichtung unproduktive Mitarbeiter*innen (z. B. Empfang/Organisation) oder
                                Auszubildende am Bonus-Topf partizipieren.
                            </p>
                            <p className="av-doc-p">
                                <strong>6. Halbjahres-Auswertung &amp; Auszahlung:</strong>
                                <br />
                                Zur Glättung saisonaler Schwankungen werden Monatsergebnisse über ein Halbjahr saldiert.
                                Schließt ein Halbjahr im Minus ab, hat dies keinerlei negative Auswirkung auf das
                                garantierte Grundgehalt. Ein positiver Gesamtsaldo wird nach Abrechnung ausgeschüttet.
                            </p>
                        </div>
                    </div>

                    <div className={`pepapers-print-break av-a4-page av-ll-page ${A4_PREVIEW_PAGE_CLASS}`}>
                        <div className="doc-panel">
                            <h4>Zusatzvereinbarung zum Arbeitsvertrag: Teamleistungslohn</h4>
                            <p className="av-doc-p">
                                <strong>1.</strong> <strong>{employeeFullName}</strong> bezieht ein festes, zuvor
                                vereinbartes Grundgehalt (aufgeteilt in produktive und ggf. unproduktive Vergütungsanteile)
                                in Höhe von {formatCurrency(effektivesBrutto)}. Der Teamleistungslohn ist eine variable
                                Zusatzvergütung und lässt dieses feste Grundgehalt unberührt.
                            </p>
                            <p className="av-doc-p">
                                <strong>2.</strong> <strong>{employeeFullName}</strong> nimmt am kollektiven
                                PreisExpert-Teamleistungslohnsystem teil. Die Auslösung eines Anspruchs setzt das
                                Überschreiten des kalkulierten Salon-Zielumsatzes (Kosten + Rücklagen) durch das
                                Gesamtergebnis des Salons voraus.
                            </p>
                            <p className="av-doc-p">
                                <strong>3.</strong> Der/die Arbeitnehmer*in bestätigt, die Funktionsbeschreibung
                                verstanden zu haben und erklärt sich mit der Verteilung proportional zum produktiven
                                Grundgehalt (bzw. dem im System definierten Teilnehmerkreis) einverstanden.
                            </p>
                            <p className="av-doc-p">
                                <strong>
                                    4. Transparenz &amp; Datenschutz (Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO):
                                </strong>{' '}
                                Der/die Arbeitnehmer*in willigt ausdrücklich ein, dass die im Salon erzielten Umsätze
                                intern zur nachvollziehbaren Berechnung und gemeinsamen Visualisierung des Teamerfolgs
                                verarbeitet und im Team offengelegt werden dürfen.
                            </p>
                            <p className="av-doc-p">
                                <strong>5.</strong> Die Auszahlung des Teamleistungslohns erfolgt als freiwillige
                                Sonderleistung. Wiederholte Auszahlungen begründen keinen Rechtsanspruch für die Zukunft
                                (Ausschluss betrieblicher Übung).
                            </p>
                            <p className="av-doc-p">
                                <strong>6.</strong>{' '}
                                {formData.isBefristet
                                    ? `Diese Zusatzvereinbarung wird auf das Geschäftsjahr ${
                                          formData.befristetBis ? formData.befristetBis.slice(0, 4) : '____'
                                      } befristet geschlossen.`
                                    : 'Diese Zusatzvereinbarung wird nicht befristet geschlossen.'}
                            </p>
                            <p className="av-doc-p">
                                <strong>7. Salvatorische Klausel:</strong> Sollte eine Bestimmung dieser Vereinbarung
                                unwirksam sein, wird die Wirksamkeit der übrigen Bestimmungen hiervon nicht berührt. Die
                                Parteien verpflichten sich, die unwirksame Bestimmung durch eine Vereinbarung zu ersetzen,
                                die der unwirksamen Bestimmung in Interessenlage und Bedeutung möglichst nahe kommt.
                            </p>
                        </div>
                        <div className="av-sig-grid">
                            <div>
                                <div className="av-sig-label">Ort, Datum</div>
                                <div className="av-sig-rule" aria-hidden="true" />
                                <div className="av-sig-role">Unterschrift Arbeitgeber/in</div>
                            </div>
                            <div>
                                <div className="av-sig-label">Ort, Datum</div>
                                <div className="av-sig-rule" aria-hidden="true" />
                                <div className="av-sig-role">Unterschrift Arbeitnehmer/in</div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className={`pepapers-print-break av-a4-page av-ll-page ${A4_PREVIEW_PAGE_CLASS}`}>
                        <div className="av-doc-section">
                            <h3>PreisExpert - Leistungslohnsystem | Bitte dem Mitarbeiter zur Info aushändigen</h3>
                        </div>
                        <div className="av-doc-section">
                            <h4>Funktionen</h4>
                            <p className="av-doc-p">
                                <strong>Leistungserfassung:</strong>
                                <br />
                                Im PreisExpert-Kalkulationstool haben alle „produktiven“ Arbeitnehmer*innen ihre eigenen
                                Seiten, wo Grundgehalt, Provisionen, Arbeitszeiten, der aktuelle Multiplikator und zu
                                erbringende Sollumsätze aufgelistet werden. Auf dieser Seite werden, aufgeteilt aufs
                                Halbjahr, Monatsspalten aufgelistet, wo die Monatsumsätze der Arbeitnehmer*innen, getrennt
                                von Bedien- und Verkaufsumsatz, eingetragen werden. Der zu erbringende Sollumsatz wird
                                durch den jeweils aktuellen Multiplikator mit dem Grundgehalt ermittelt.{' '}
                                {LEISTUNGSLOHN_MULTIPLIKATOR_HINWEIS}
                            </p>
                            <p className="av-doc-p">
                                <strong>Umsätze:</strong>
                                <br />
                                Die Mitarbeiterumsätze werden durch die Arbeitnehmer*innen zum Monatsende in die
                                Monatsspalten eingetragen. Umsatzüberschüsse, aber auch Umsatzdifferenzen werden somit
                                automatisch errechnet und ausgewertet, so ist zu erkennen, ob die Umsätze im Plus oder
                                Minus liegen.
                            </p>
                            <p className="av-doc-p">
                                <strong>Erholungsurlaub:</strong>
                                <br />
                                Gewährter Erholungsurlaub wird pro Monat eingetragen und wird anhand der erbrachten
                                durchschnittlichen Bedienumsätze als virtueller Umsatz gutgeschrieben. So ist
                                gewährleistet, dass die Arbeitnehmer*innen keinen Provisionsverlust erleiden, wenn diese
                                den wohlverdienten Erholungsurlaub antreten.
                                <br />
                                <span className="italic text-xs text-slate-600">
                                    Wichtig: Für Krankheitstage gibt es keinen Ausgleich.
                                </span>
                            </p>
                            <p className="av-doc-p">
                                <strong>Unproduktive Zeiten:</strong>
                                <br />
                                Sollten die Arbeitnehmer*innen wichtige Tätigkeiten für das Unternehmen ausführen
                                (Ausbildung, Seminare etc.) können diese Zeiten eingetragen werden. Diese Zeiten werden
                                anhand des stündlichen Sollumsatzes als Umsatz hochgerechnet und vom Sollumsatz
                                abgezogen.
                            </p>
                            <p className="av-doc-p">
                                <strong>Leistungslohn &amp; Verkaufsprovisionen:</strong>
                                <br />
                                Umsatzüberschüsse werden monatlich erfasst und mit den jeweils aktuellen, variablen
                                Provisionssätzen für Dienstleistung und Verkauf hinterlegt.{' '}
                                {LEISTUNGSLOHN_PROVISION_HINWEIS}
                            </p>
                            <p className="av-doc-p">
                                <strong>Provisionen (Auswertung und Auszahlung):</strong>
                                <br />
                                Um ein für beide Seiten faires System zu generieren, werden Minus- und Plusauswertungen
                                sechs Monate (Halbjahr) gesammelt und aufgerechnet. Sollten die Arbeitnehmer*innen
                                insgesamt ins Minus geraten, hat dies keine finanziellen Auswirkungen. Das Grundgehalt
                                bleibt bestehen. Haben die Arbeitnehmer*innen aber insgesamt einen positiven Wert, wird
                                dieser nach Absprache ausgezahlt.
                            </p>
                        </div>
                    </div>

                    <div className={`pepapers-print-break av-a4-page av-ll-page ${A4_PREVIEW_PAGE_CLASS}`}>
                        <div className="doc-panel">
                            <h4>Zusatzvereinbarung: Bitte dem Arbeitsvertrag zufügen</h4>
                            <p className="av-doc-p">
                                <strong>{employeeFullName}</strong> bezieht ein festes zuvor durch beide Parteien
                                (Arbeitgeber/Arbeitnehmer) ausgehandeltes Grundgehalt in Höhe von{' '}
                                {formatCurrency(effektivesBrutto)}. Ein Leistungslohn hat keinen Einfluss auf diesen Wert.
                                Sollte es hier etwaiger Änderungen bedürfen, werden diese außerhalb dieses Systems
                                vereinbart.
                            </p>
                            <p className="av-doc-p">
                                <strong>{employeeFullName}</strong> ist einverstanden, seine/ihre persönlichen Umsätze mit
                                dem oben beschriebenen Leistungslohnsystem aus- und aufzuwerten.
                            </p>
                            <p className="av-doc-p">
                                Der/die Arbeitnehmer/in hat die Angaben in der oberen Funktionsbeschreibung verstanden und
                                ist mit deren Umsetzung einverstanden.
                            </p>
                            <p className="av-doc-p">
                                <strong>
                                    Transparenz &amp; Datenschutz (Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO):
                                </strong>{' '}
                                Der/die Arbeitnehmer*in willigt ausdrücklich ein, dass die im Salon erzielten Umsätze
                                intern zur nachvollziehbaren Berechnung und Auswertung der individuellen
                                Leistungsvergütung verarbeitet und erfasst werden dürfen.
                            </p>
                            <p className="av-doc-p">
                                Die gewährten Leistungslöhne sind eine Sonderform der Motivation, es entstehen aber keine
                                generellen Verpflichtungen der Auszahlung/Auswertung.
                            </p>
                            <p className="av-doc-p">
                                {formData.isBefristet
                                    ? `Das Leistungslohnsystem wird auf das Geschäftsjahr ${
                                          formData.befristetBis ? formData.befristetBis.slice(0, 4) : '____'
                                      } befristet, um sicherzustellen das es zu keiner dauerhaften Einführung kommen kann.`
                                    : 'Das Leistungslohnsystem wird auf das Geschäftsjahr nicht befristet, um sicherzustellen das es zu keiner dauerhaften Einführung kommen kann.'}
                            </p>
                            <h4>Sonstige Bestimmungen</h4>
                            <p className="av-doc-p">
                                Sollte eine Bestimmung dieser Vereinbarung unwirksam sein, wird die Wirksamkeit der
                                übrigen Bestimmungen hiervon nicht berührt. Die Parteien verpflichten sich, die
                                unwirksame Bestimmung durch eine Vereinbarung zu ersetzen, die der unwirksamen Bestimmung
                                in Interessenlage und Bedeutung möglichst nahe kommt.
                            </p>
                        </div>
                        <div className="av-sig-grid">
                            <div>
                                <div className="av-sig-label">Ort, Datum</div>
                                <div className="av-sig-rule" aria-hidden="true" />
                                <div className="av-sig-role">Unterschrift Arbeitgeber/in</div>
                            </div>
                            <div>
                                <div className="av-sig-label">Ort, Datum</div>
                                <div className="av-sig-rule" aria-hidden="true" />
                                <div className="av-sig-role">Unterschrift Arbeitnehmer/in</div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );

    const renderStep4Document = () => (
        <>
            <style>{AV_CONTRACT_PDF_CSS}</style>
            <div
                ref={printContainerRef}
                id="pepapers-arbeitsvertrag-print"
                className="w-full h-auto overflow-visible flex flex-col items-center gap-4 bg-slate-200/40 dark:bg-black/40 py-6 print:bg-white print:py-0"
            >
                {renderLocalFallbackMainContract()}
                {leistungslohnAktiv ? renderLocalFallbackPage2() : null}
            </div>
        </>
    );

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 4 ? 'max-w-4xl' : currentStep === 2 ? 'max-w-5xl' : 'max-w-2xl';

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div
                className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0"
            >
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleDevTestFill}
                        className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:text-[#E6C299] bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg px-2.5 py-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        title="Testdaten laden"
                    >
                        Test
                    </button>

                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                        <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8">
                            Arbeitsvertrag
                        </h3>
                        <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                            Schritt {currentStep} von 4: {STEP_LABELS[currentStep]}
                        </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className={LABEL_CLASS}>Vorname</label>
                                        <input
                                            type="text"
                                            value={formData.vorname}
                                            onChange={(e) => updateField('vorname', e.target.value)}
                                            className={getInputClass(INPUT_CLASS, 'vorname')}
                                        />
                                        {renderFieldError('vorname')}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Nachname</label>
                                        <input
                                            type="text"
                                            value={formData.nachname}
                                            onChange={(e) => updateField('nachname', e.target.value)}
                                            className={getInputClass(INPUT_CLASS, 'nachname')}
                                        />
                                        {renderFieldError('nachname')}
                                    </div>
                                </div>
                                <div>
                                    <label className={LABEL_CLASS}>Straße & Hausnummer</label>
                                    <input
                                        type="text"
                                        value={formData.strasse}
                                        onChange={(e) => updateField('strasse', e.target.value)}
                                        className={getInputClass(INPUT_CLASS, 'strasse')}
                                    />
                                    {renderFieldError('strasse')}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label className={LABEL_CLASS}>PLZ</label>
                                        <input
                                            type="text"
                                            value={formData.plz}
                                            onChange={(e) => updateField('plz', e.target.value)}
                                            className={getInputClass(INPUT_CLASS, 'plz')}
                                        />
                                        {renderFieldError('plz')}
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className={LABEL_CLASS}>Ort</label>
                                        <input
                                            type="text"
                                            value={formData.ort}
                                            onChange={(e) => updateField('ort', e.target.value)}
                                            className={getInputClass(INPUT_CLASS, 'ort')}
                                        />
                                        {renderFieldError('ort')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <div>
                                    <label className={LABEL_CLASS}>Eintrittsdatum</label>
                                    <div
                                        className="relative flex items-center cursor-pointer"
                                        onClick={() => openDatePicker(dateInputRef)}
                                    >
                                        <input
                                            ref={dateInputRef}
                                            type="date"
                                            value={formData.eintrittsdatum}
                                            onChange={(e) => updateField('eintrittsdatum', e.target.value)}
                                            className={getInputClass(`${INPUT_CLASS} pr-10 cursor-pointer`, 'eintrittsdatum')}
                                        />
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDatePicker(dateInputRef);
                                            }}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-pe-muted dark:text-slate-400 hover:text-[#E6C299] transition-colors p-1"
                                            aria-label="Kalender öffnen"
                                        >
                                            <Calendar size={16} strokeWidth={2} />
                                        </button>
                                    </div>
                                    {renderFieldError('eintrittsdatum')}
                                </div>
                                <div>
                                    <label className={LABEL_CLASS}>Beschäftigungsart</label>
                                    <select
                                        value={formData.beschaeftigungsart}
                                        onChange={(e) => handleBeschaeftigungsartChange(e.target.value as Beschaeftigungsart)}
                                        className={INPUT_CLASS}
                                    >
                                        <option value="Vollzeit">Vollzeit</option>
                                        <option value="Teilzeit">Teilzeit</option>
                                        <option value="Minijob">Minijob</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={LABEL_CLASS}>Befristung Leistungslohnsystem</label>
                                    {renderSegmentedControl(
                                        formData.isBefristet ? 'befristet' : 'unbefristet',
                                        [
                                            { key: 'unbefristet', label: 'Unbefristet' },
                                            { key: 'befristet', label: 'Befristet' },
                                        ],
                                        (key) => {
                                            const isBefristet = key === 'befristet';
                                            updateField('isBefristet', isBefristet);
                                            if (isBefristet) {
                                                if (!formData.befristungMonate) {
                                                    updateField('befristungMonate', '12');
                                                }
                                            } else {
                                                updateField('befristetBis', '');
                                            }
                                            clearFieldError('befristungMonate');
                                            clearFieldError('befristetBis');
                                        }
                                    )}
                                </div>
                                {formData.isBefristet && (
                                    <div>
                                        <label className={LABEL_CLASS}>Befristungsdauer (Monate)</label>
                                        {renderStepperInput('befristungMonate', formData.befristungMonate, 1, { min: 1, max: 12 })}
                                        <p className={HELPER_TEXT_CLASS}>
                                            1–12 Monate (z. B. 6 = ein halbes Jahr, 12 = ein Jahr)
                                        </p>
                                        {renderFieldError('befristungMonate')}
                                        <p className={`${HELPER_TEXT_CLASS} mt-2`}>
                                            Endet automatisch am:{' '}
                                            <span className="text-pe-accent-text dark:text-[#E6C299]">
                                                {formData.befristetBis
                                                    ? formatGermanDate(formData.befristetBis)
                                                    : 'Bitte zuerst Eintrittsdatum wählen'}
                                            </span>
                                        </p>
                                        {renderFieldError('befristetBis')}
                                    </div>
                                )}
                                <ArbeitszeitPlanEditor
                                    title="Wöchentlicher Arbeitsplan"
                                    plan={arbeitsplan}
                                    onChange={setArbeitsplan}
                                    required
                                    isFieldInvalid={isFieldInvalid}
                                    onClearError={clearFieldError}
                                    wochensatz={wochensatz}
                                    targetWochenstunden={
                                        Number(formData.wochenstunden) > 0
                                            ? Number(formData.wochenstunden)
                                            : null
                                    }
                                />
                                {isFieldInvalid('arbeitsplan') && (
                                    <p className="text-[10px] text-red-400 mt-1">
                                        Bitte mindestens einen vollständigen Arbeitstag mit Wochentag, Von–Bis und Pause eintragen.
                                    </p>
                                )}
                                <div className="rounded-lg border border-[#E6C299]/30 bg-[#E6C299]/10 p-4 space-y-3">
                                    <div>
                                        <label className={LABEL_CLASS}>Wochenstunden (Vertragswert)</label>
                                        {renderStepperInput('wochenstunden', formData.wochenstunden, 0.25)}
                                        <p className={HELPER_TEXT_CLASS}>
                                            {berechnetePlanWochenstunden > 0 && (
                                                <>
                                                    Aus Arbeitsplan berechnet:{' '}
                                                    <span className="text-pe-accent-text dark:text-[#E6C299] font-medium">
                                                        {formatStdForDisplay(berechnetePlanWochenstunden)} Std./Woche
                                                    </span>
                                                    {' · '}
                                                </>
                                            )}
                                            Monatsstunden (× Wochensatz {formatNumber2(wochensatz)}):{' '}
                                            <span className="text-pe-accent-text dark:text-[#E6C299] font-medium">
                                                {formatNumber2(berechneteMonatsstunden)}
                                            </span>
                                            {' '}(Dezimalwerte wie 30,77 möglich)
                                        </p>
                                        {renderFieldError('wochenstunden')}
                                    </div>
                                </div>
                                <div>
                                    <label className={LABEL_CLASS}>Probezeit</label>
                                    {renderSegmentedControl(
                                        formData.hasProbezeit ? 'mit_probezeit' : 'ohne_probezeit',
                                        [
                                            { key: 'mit_probezeit', label: 'Mit Probezeit' },
                                            { key: 'ohne_probezeit', label: 'Ohne Probezeit' },
                                        ],
                                        (key) => {
                                            const hasProbezeit = key === 'mit_probezeit';
                                            updateField('hasProbezeit', hasProbezeit);
                                            if (hasProbezeit && !formData.probezeitMonate) {
                                                updateField('probezeitMonate', '6');
                                            }
                                            clearFieldError('probezeitMonate');
                                            clearFieldError('probezeitEnde');
                                        }
                                    )}
                                </div>
                                {formData.hasProbezeit && (
                                    <div>
                                        <label className={LABEL_CLASS}>Probezeit (Monate, max. 6)</label>
                                        {renderStepperInput('probezeitMonate', formData.probezeitMonate, 1, { min: 1, max: 6 })}
                                        <p className={HELPER_TEXT_CLASS}>
                                            Probezeit endet am:{' '}
                                            <span className="text-pe-accent-text dark:text-[#E6C299]">
                                                {probezeitEnde
                                                    ? formatGermanDate(probezeitEnde)
                                                    : 'Bitte zuerst Eintrittsdatum wählen'}
                                            </span>
                                        </p>
                                        {renderFieldError('probezeitMonate')}
                                        {renderFieldError('probezeitEnde')}
                                    </div>
                                )}
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Vertraglicher Urlaub bei Vollzeit (Tage)</label>
                                            {renderStepperInput('urlaubBasisVollzeit', formData.urlaubBasisVollzeit, 1)}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Reguläre Betriebswoche (BAG-Basis)</label>
                                            {renderSegmentedControl(
                                                formData.betriebWochentage,
                                                [
                                                    { key: '5' as BetriebWochentage, label: '5-Tage (260/Jahr)' },
                                                    { key: '6' as BetriebWochentage, label: '6-Tage (312/Jahr)' },
                                                ],
                                                (key) => updateField('betriebWochentage', key)
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Jahresarbeitstage des Mitarbeiters</label>
                                        {renderStepperInput('jahresarbeitstage', formData.jahresarbeitstage, 1, { min: 1 })}
                                        <p className={HELPER_TEXT_CLASS}>
                                            {berechnetePlanJahresarbeitstage > 0 ? (
                                                <>
                                                    Aus Arbeitsplan berechnet:{' '}
                                                    <span className="text-pe-accent-text dark:text-[#E6C299] font-medium">
                                                        {berechnetePlanJahresarbeitstage} Tage/Jahr
                                                    </span>
                                                    {' '}(wöchentlich × 52, Monatsrhythmus × 12) – manuell überschreibbar
                                                </>
                                            ) : (
                                                <>Wird aus dem Arbeitsplan summiert (BAG-Jahresmethode)</>
                                            )}
                                        </p>
                                    </div>
                                    <div className="rounded-lg border border-[#E6C299]/30 bg-[#E6C299]/10 p-4">
                                        <p className="text-xs text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1">
                                            Automatisch berechneter Urlaubsanspruch (BAG-Standard)
                                        </p>
                                        <p className="text-pe-text dark:text-slate-200 text-sm leading-relaxed">
                                            Der Mitarbeiter erhält im Vertrag{' '}
                                            <span className="text-3xl font-black text-pe-accent-text dark:text-[#E6C299] mx-1 align-middle">
                                                {berechneterUrlaubstage}
                                            </span>
                                            <span className="text-pe-accent-text dark:text-[#E6C299] font-semibold">Urlaubstage</span> pro Kalenderjahr.
                                        </p>
                                        <p className={`${HELPER_TEXT_CLASS} mt-2`}>
                                            Formel: ({formData.urlaubBasisVollzeit || '20'} ÷ {betriebJahresarbeitstage}) ×{' '}
                                            {formData.jahresarbeitstage || '0'} Jahresarbeitstage
                                            {urlaubRawBerechnet > 0 && urlaubRawBerechnet !== berechneterUrlaubstage && (
                                                <>
                                                    {' '}
                                                    → rechnerisch {formatNumber2(urlaubRawBerechnet)}, gerundet nach § 5 Abs. 2 BUrlG
                                                </>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <SondervereinbarungenField
                                    value={sondervereinbarungen}
                                    onChange={setSondervereinbarungen}
                                    dokumentKontext="vertrag"
                                />
                            </div>
                        )}

                        {currentStep === 3 && (
                            <div className="space-y-5">
                                {renderLohnWarnBanner()}
                                <div>
                                    <label className={LABEL_CLASS}>Gehaltsbasis</label>
                                    {renderSegmentedControl(gehaltMode, [
                                        { key: 'brutto', label: 'Monatsbrutto' },
                                        { key: 'stundenlohn', label: 'Stundenlohn' },
                                    ], (mode) => {
                                        setGehaltMode(mode);
                                        setInvalidFields((prev) => {
                                            const next = new Set(prev);
                                            next.delete('bruttoGrundgehalt');
                                            next.delete('stundenlohn');
                                            return next;
                                        });
                                    })}
                                </div>

                                {gehaltMode === 'brutto' ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Brutto-Grundgehalt ({currencySuffix})</label>
                                            {renderCurrencyInput('bruttoGrundgehalt', formData.bruttoGrundgehalt)}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Berechneter Stundenlohn</label>
                                            <div className="relative flex items-center">
                                                <input
                                                    type="text"
                                                    readOnly
                                                    value={berechneteMonatsstunden > 0 ? formatNumber2(effektiverStundenlohn) : '—'}
                                                    className={`${READONLY_INPUT_CLASS} pr-10`}
                                                />
                                                <span className="absolute right-4 text-stone-500 dark:text-slate-500 text-sm pointer-events-none">{currencySuffix}</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Stundenlohn ({currencySuffix})</label>
                                            {renderCurrencyInput('stundenlohn', formData.stundenlohn)}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Berechnetes Monatsbrutto</label>
                                            <div className="relative flex items-center">
                                                <input
                                                    type="text"
                                                    readOnly
                                                    value={effektivesBrutto > 0 ? formatNumber2(effektivesBrutto) : '—'}
                                                    className={`${READONLY_INPUT_CLASS} pr-10`}
                                                />
                                                <span className="absolute right-4 text-stone-500 dark:text-slate-500 text-sm pointer-events-none">{currencySuffix}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <p className="text-[10px] text-stone-500 dark:text-slate-500">
                                    Basis: {formatNumber2(berechneteMonatsstunden)} Monatsstunden
                                    ({formData.wochenstunden || '0'} Std./Woche × Wochensatz {formatNumber2(wochensatz)}).
                                </p>

                                <div className="rounded-xl border border-pe-border dark:border-slate-700 bg-stone-100/60 dark:bg-black/40 p-4">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <p className="text-sm font-bold text-pe-text dark:text-white">Zusatzvereinbarung Leistungslohn aktivieren</p>
                                            <p className="text-xs text-stone-500 dark:text-slate-500 mt-1">
                                                {leistungslohnVariant === 'team'
                                                    ? 'Bindet das Teamleistungslohnsystem ein – Bonus nur bei Überschreiten des Salon-Zielumsatzes, Verteilung über den Team-Bonus-Topf.'
                                                    : 'Bindet das Leistungslohnsystem ein – ohne feste Multiplikator- oder Provisionshöhe. Es gilt jeweils der aktuelle Multiplikator sowie variable Provisionssätze.'}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setLeistungslohnAktiv((prev) => !prev)}
                                            className={`relative w-12 h-6 rounded-full transition-colors shrink-0 border ${
                                                leistungslohnAktiv
                                                    ? 'bg-[#E6C299]/30 border-[#E6C299]/50'
                                                    : 'bg-slate-800 border-pe-border-strong dark:border-slate-600'
                                            }`}
                                            aria-pressed={leistungslohnAktiv}
                                        >
                                            <span
                                                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform ${
                                                    leistungslohnAktiv
                                                        ? 'translate-x-6 bg-[#E6C299]'
                                                        : 'translate-x-0 bg-slate-500'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </div>

                                {leistungslohnAktiv && (
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-widest text-pe-muted dark:text-slate-500 mb-2">
                                                Leistungslohn-Variante
                                            </p>
                                            <LeistungslohnVariantToggle
                                                variant={leistungslohnVariant}
                                                onChange={setLeistungslohnVariant}
                                            />
                                        </div>
                                        <div className="text-xs text-pe-muted dark:text-slate-400 leading-relaxed border border-[#E6C299]/20 bg-[#E6C299]/5 rounded-lg p-3">
                                            {leistungslohnVariant === 'team' ? (
                                                <>
                                                    Die Zusatzvereinbarung beschreibt das Teamleistungslohnsystem: Salon-Zielumsatz als Schwelle,
                                                    gemeinsamer Team-Bonus-Topf und Verteilung proportional zum produktiven Grundgehalt —
                                                    inklusive Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO zur Teamerfolgs-Visualisierung.
                                                </>
                                            ) : (
                                                <>
                                                    Die Zusatzvereinbarung beschreibt das Leistungslohnsystem, ohne feste Zahlen festzuschreiben.
                                                    {LEISTUNGSLOHN_MULTIPLIKATOR_HINWEIS} {LEISTUNGSLOHN_PROVISION_HINWEIS}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {currentStep === 4 && (
                            <div className="w-full min-h-0 pr-2 mb-6">
                                {renderLohnWarnBanner()}
                                {apiError && (
                                    <p className="text-[10px] text-amber-400 mb-3 w-full text-center shrink-0">
                                        KI-Generierung fehlgeschlagen — lokales Standard-Muster wird angezeigt.
                                    </p>
                                )}
                                {renderStep4Document()}
                            </div>
                        )}
                    </div>

                    {currentStep === 4 && (
                        <PePapersDisclaimerGate
                            accepted={disclaimerAccepted}
                            onAcceptedChange={setDisclaimerAccepted}
                            text="Ich bestätige ausdrücklich, dass ich den rechtlichen Hinweis verstanden habe. Mir ist bekannt, dass es sich hierbei um ein automatisiertes Standard-Muster und keine individuelle Rechtsberatung handelt. Ich wurde ausdrücklich darauf hingewiesen, das generierte Dokument vor der Unterzeichnung zur finalen Absicherung auf Rechtssicherheit durch eine zuständige Innung, die Handwerkskammer (HWK) oder einen Fachanwalt für Arbeitsrecht prüfen zu lassen. Die Verwendung erfolgt auf rein eigene Gefahr und Verantwortung, jegliche Haftung des Systembetreibers ist ausdrücklich ausgeschlossen."
                        />
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        <div className="flex flex-wrap gap-3">
                        {currentStep > 1 && (
                            <button
                                type="button"
                                onClick={handleZurueck}
                                disabled={isLoading}
                                className={`flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors ${
                                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                Zurück
                            </button>
                        )}

                        {currentStep < 3 ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => void handleArchive(true)}
                                    disabled={isSaving}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                >
                                    {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleWeiter}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors"
                                >
                                    Weiter
                                </button>
                            </>
                        ) : currentStep === 3 ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => void handleArchive(true)}
                                    disabled={isSaving}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                >
                                    Später fortsetzen
                                </button>
                                <button
                                    type="button"
                                    onClick={handleGenerierenClick}
                                    disabled={isLoading}
                                    className={`flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors ${
                                        isLoading ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                >
                                    {isLoading ? 'Generiert...' : 'Vertrag generieren'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => void handleArchive(true)}
                                    disabled={isSaving}
                                    className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                >
                                    {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handleArchive(false)}
                                    disabled={isActionLocked || isSaving}
                                    className={getActionButtonClass(
                                        'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                    )}
                                >
                                    {isSaving && archiveFeedback !== 'draft'
                                        ? 'Speichert...'
                                        : archiveFeedback === 'success'
                                        ? 'Gespeichert! ✓'
                                        : 'Im Archiv speichern'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handleDownloadPdf()}
                                    disabled={isActionLocked || isPdfDownloading}
                                    className={getActionButtonClass(
                                        'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors'
                                    )}
                                >
                                    {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handlePrintIsolated}
                                    disabled={isActionLocked}
                                    className={getActionButtonClass(
                                        'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                    )}
                                >
                                    Vertrag ausdrucken
                                </button>
                            </>
                        )}
                        </div>
                        {currentStep === 4 && archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">Archivierung fehlgeschlagen. Bitte erneut versuchen.</p>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default ArbeitsvertragModal;
